import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Toaster } from '../components/ui/toaster';
import { ArrowRight, ArrowLeft, CheckCircle, Building2, Sparkles, Quote } from 'lucide-react';

const caseStudiesData = {
  engenoil: {
    title: 'SAP S/4HANA Migration for EngenOil',
    client: 'EngenOil',
    industry: 'Petroleum',
    category: 'SAP S/4HANA Migration',
    image: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=800&q=80',
    description: 'EngenOil, a major player in the petroleum industry, faced increasing challenges with its legacy ERP system. The company was experiencing difficulties with real-time data processing, slow response times, and inefficiencies in managing critical functions like finance, supply chain, and asset management.',
    challenge: 'As the business grew and the industry became more dynamic, EngenOil recognized the need for a modern ERP system to streamline operations, improve efficiency, and enable real-time decision-making. The legacy system was creating bottlenecks in critical business processes and limiting growth potential.',
    solution: 'UZVI Services delivered a comprehensive SAP S/4HANA migration that transformed their entire business operations. Our team implemented real-time analytics, automated key processes, and created a unified platform for all business functions. The migration was executed with minimal downtime and zero data loss.',
    results: [
      '40% improvement in real-time data processing',
      '30% reduction in operational costs',
      'Streamlined finance and supply chain operations',
      'Enhanced asset management capabilities',
      'Real-time visibility across all business units'
    ],
    technologies: ['SAP S/4HANA', 'SAP Fiori', 'SAP HANA DB', 'SAP Integration Suite'],
    duration: '18 months',
    testimonial: {
      quote: 'UZVI Services transformed our operations completely. The S/4HANA migration was seamless and the results exceeded our expectations.',
      author: 'Operations Director',
      company: 'EngenOil'
    }
  },
  sanofi: {
    title: 'SAP S/4HANA Migration at Sanofi',
    client: 'Sanofi',
    industry: 'Pharmaceutical',
    category: 'SAP S/4HANA Migration',
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800&q=80',
    description: 'Sanofi, a leading global pharmaceutical company headquartered in Paris, is renowned for its extensive product portfolio across prescription medicines, consumer healthcare, and vaccines. With operations in over 100 countries, Sanofi needed a robust digital foundation.',
    challenge: 'To continue driving innovation and improve operational efficiency, Sanofi embarked on a large-scale digital transformation journey. The company needed to integrate disparate systems across global operations while maintaining strict compliance with pharmaceutical regulations.',
    solution: 'UZVI Services partnered with Sanofi to execute a seamless SAP S/4HANA implementation across their global operations. We implemented a phased approach that ensured minimal disruption to ongoing business activities while enabling rapid adoption of new capabilities.',
    results: [
      'Successfully integrated global business operations',
      'Improved supply chain visibility across 100+ countries',
      'Enhanced compliance and regulatory reporting',
      'Reduced time-to-market for new products',
      'Unified data platform for better decision making'
    ],
    technologies: ['SAP S/4HANA', 'SAP GRC', 'SAP IBP', 'SAP Analytics Cloud'],
    duration: '24 months',
    testimonial: {
      quote: 'The UZVI team demonstrated exceptional expertise in pharmaceutical industry requirements. Their attention to compliance and regulatory needs was outstanding.',
      author: 'IT Director',
      company: 'Sanofi'
    }
  },
  ascensus: {
    title: 'SAP S/4 HANA Migration for Ascensus',
    client: 'Ascensus',
    industry: 'Financial Services',
    category: 'SAP S/4HANA Migration',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80',
    description: 'Ascensus, a prominent American financial services provider, specializes in retirement, college savings, and health savings solutions. Founded in 1980 and headquartered in Dresher, Pennsylvania, Ascensus has grown to manage over $400 billion in assets.',
    challenge: 'As a company with a complex, data-intensive operational structure, Ascensus sought a robust solution to streamline processes, improve data accuracy, and drive scalable growth while maintaining the highest standards of financial security.',
    solution: 'UZVI Services implemented a tailored SAP S/4HANA solution that addressed the unique needs of financial services. Special emphasis was placed on data security, regulatory compliance, and scalability to support continued growth.',
    results: [
      'Managing over $400 billion in assets efficiently',
      'Improved data accuracy and reporting',
      'Enhanced scalability for future growth',
      'Strengthened compliance framework',
      '50% faster financial reporting'
    ],
    technologies: ['SAP S/4HANA Finance', 'SAP BPC', 'SAP GRC', 'SAP Fiori'],
    duration: '20 months',
    testimonial: {
      quote: 'UZVI understood the unique challenges of financial services. Their solution has positioned us for continued growth while maintaining our commitment to security.',
      author: 'CTO',
      company: 'Ascensus'
    }
  },
  'retail-chain': {
    title: 'Cloud Migration for Global Retail Chain',
    client: 'Global Retail Corp',
    industry: 'Retail',
    category: 'Cloud Solutions',
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80',
    description: 'A leading global retail chain with operations in 25 countries needed to modernize their infrastructure to support rapid e-commerce growth and compete in the digital marketplace.',
    challenge: 'The client faced challenges with scalability during peak seasons, high infrastructure costs, and inability to quickly deploy new features. Their on-premise infrastructure could not keep pace with the demands of modern retail.',
    solution: 'UZVI Services designed and executed a comprehensive Azure cloud migration strategy. We implemented microservices architecture, automated scaling solutions, and established a robust CI/CD pipeline for rapid feature deployment.',
    results: [
      '60% reduction in infrastructure costs',
      '99.99% uptime during peak seasons',
      '3x faster feature deployment',
      'Seamless handling of 10x traffic spikes',
      'Global CDN for improved user experience'
    ],
    technologies: ['Microsoft Azure', 'Kubernetes', 'Docker', 'Azure DevOps', 'Terraform'],
    duration: '12 months',
    testimonial: {
      quote: 'The cloud migration was a game-changer for our business. We can now scale instantly during Black Friday and holiday seasons without any concerns.',
      author: 'VP of Technology',
      company: 'Global Retail Corp'
    }
  },
  'healthcare-app': {
    title: 'Healthcare Mobile App Development',
    client: 'MedConnect Health',
    industry: 'Healthcare',
    category: 'Mobile Development',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&q=80',
    description: 'MedConnect Health required a HIPAA-compliant mobile application to connect patients with healthcare providers and manage medical records securely across their network of clinics.',
    challenge: 'Building a secure, user-friendly mobile app that complies with strict healthcare regulations while providing seamless experience for both patients and doctors. The app needed to handle sensitive medical data with the highest security standards.',
    solution: 'UZVI Services developed a cross-platform mobile application using React Native with end-to-end encryption. We implemented comprehensive security measures including biometric authentication, encrypted data storage, and secure API communications.',
    results: [
      '100% HIPAA compliance achieved',
      '50,000+ active users in first 6 months',
      '4.8 star rating on app stores',
      '40% reduction in appointment no-shows',
      'Secure telehealth consultations enabled'
    ],
    technologies: ['React Native', 'Node.js', 'MongoDB', 'AWS', 'WebRTC'],
    duration: '8 months',
    testimonial: {
      quote: 'UZVI delivered a beautiful, secure app that our patients love. The attention to HIPAA compliance gave us complete confidence in the solution.',
      author: 'Chief Medical Officer',
      company: 'MedConnect Health'
    }
  },
  'ecommerce-platform': {
    title: 'E-Commerce Platform Development',
    client: 'TechMart Online',
    industry: 'E-Commerce',
    category: 'Web Development',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80',
    description: 'TechMart Online needed a modern, scalable e-commerce platform to support their growing electronics retail business and compete with major online retailers.',
    challenge: 'The existing platform could not handle increasing traffic, had poor mobile experience, and lacked modern features like real-time inventory management. Page load times were causing significant cart abandonment.',
    solution: 'UZVI Services built a custom e-commerce solution using Next.js and Node.js. We implemented integrated inventory management, multiple payment gateways, real-time analytics, and a headless CMS for content management.',
    results: [
      '200% increase in online sales',
      '45% improvement in mobile conversion',
      'Real-time inventory across 50+ warehouses',
      '2 second average page load time',
      '60% reduction in cart abandonment'
    ],
    technologies: ['Next.js', 'Node.js', 'PostgreSQL', 'Redis', 'Stripe', 'Elasticsearch'],
    duration: '10 months',
    testimonial: {
      quote: 'Our new platform has completely transformed our online business. Sales have doubled and customers love the new experience.',
      author: 'CEO',
      company: 'TechMart Online'
    }
  }
};

const CaseStudyDetailPage = () => {
  const { caseStudyId } = useParams();
  const pageRef = useRef(null);
  const caseStudy = caseStudiesData[caseStudyId] || caseStudiesData.engenoil;

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = pageRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [caseStudyId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30" ref={pageRef}>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="hero-orb hero-orb-1 opacity-40"></div>
            <div className="hero-orb hero-orb-2 opacity-30"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <Link to="/case-studies" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#ff6b35] mb-8 reveal">
              <ArrowLeft className="w-4 h-4" />
              Back to Case Studies
            </Link>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="reveal-left">
                <div className="flex flex-wrap gap-3 mb-6">
                  <span className="px-3 py-1 bg-[#ff6b35] text-white text-sm font-semibold rounded-full">
                    {caseStudy.category}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                    {caseStudy.industry}
                  </span>
                </div>
                
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                  {caseStudy.title}
                </h1>

                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  {caseStudy.description}
                </p>

                <div className="flex flex-wrap gap-6 text-sm">
                  <div>
                    <span className="text-gray-500">Client</span>
                    <p className="font-semibold text-gray-900">{caseStudy.client}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Duration</span>
                    <p className="font-semibold text-gray-900">{caseStudy.duration}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Industry</span>
                    <p className="font-semibold text-gray-900">{caseStudy.industry}</p>
                  </div>
                </div>
              </div>

              <div className="reveal-right relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src={caseStudy.image} 
                    alt={caseStudy.title}
                    className="w-full h-[400px] object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Challenge & Solution */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              <div className="reveal-left">
                <span className="inline-block px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-semibold mb-4">
                  The Challenge
                </span>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">What They Faced</h2>
                <p className="text-gray-600 leading-relaxed">{caseStudy.challenge}</p>
              </div>
              
              <div className="reveal-right">
                <span className="inline-block px-4 py-2 bg-green-50 text-green-600 rounded-full text-sm font-semibold mb-4">
                  Our Solution
                </span>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Helped</h2>
                <p className="text-gray-600 leading-relaxed">{caseStudy.solution}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="inline-block px-4 py-2 bg-white text-[#ff6b35] rounded-full text-sm font-semibold shadow-sm mb-4">
                Results
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Key Outcomes
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {caseStudy.results.map((result, index) => (
                <div 
                  key={index}
                  className="reveal-scale bento-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-gray-700 font-medium">{result}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies Used */}
        <section className="py-20 bg-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff6b35] rounded-full filter blur-[128px] opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12 reveal">
              <span className="inline-block px-4 py-2 bg-white/10 text-[#ff6b35] rounded-full text-sm font-semibold mb-4">
                Technologies
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Tech Stack Used
              </h2>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              {caseStudy.technologies.map((tech, i) => (
                <div 
                  key={tech}
                  className="reveal-scale px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full text-white font-medium border border-white/20"
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  {tech}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        {caseStudy.testimonial && (
          <section className="py-20 bg-white">
            <div className="max-w-4xl mx-auto px-6 lg:px-8">
              <div className="reveal bento-card text-center relative">
                <Quote className="w-12 h-12 text-[#ff6b35]/20 mx-auto mb-6" />
                <p className="text-xl md:text-2xl text-gray-700 italic mb-8 leading-relaxed">
                  "{caseStudy.testimonial.quote}"
                </p>
                <div>
                  <p className="font-semibold text-gray-900">{caseStudy.testimonial.author}</p>
                  <p className="text-gray-500">{caseStudy.testimonial.company}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-[#ff6b35] to-[#ff8f5c] relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30"></div>
          
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <Sparkles className="w-12 h-12 text-white/80 mx-auto mb-6 reveal" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 reveal">
              Start Your Success Story
            </h2>
            <p className="text-white/90 text-lg mb-10 reveal">
              Ready to achieve similar results for your business? Let's talk.
            </p>
            <Link 
              to="/contact"
              className="reveal inline-flex items-center gap-3 bg-white text-[#ff6b35] px-8 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
            >
              Contact Us Today
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default CaseStudyDetailPage;
